import { Component, OnInit, Signal, computed, inject } from '@angular/core';

import { InstructionsComponent } from '@r1-compass-library/components';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute } from '@angular/router';
import { ContentAsset, Play } from '@r1-compass-library/models';
import { AssetStateMessageHandlingService } from '@r1-compass-library/services/asset-state-message-handling.service';
import { filterNil } from 'ngxtension/filter-nil';
import { SelectedContentAssetStore } from '../store/selected-content-asset.store';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { PlayService } from '@r1-compass-library/services';
import { MatOption, MatSelect } from '@angular/material/select';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { take } from 'rxjs';
import {
    FormUnsavedChangesWarningEntity,
    noopFormSaveMutationResult,
} from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { filterArray } from 'ngxtension/filter-array';
import { SaveGuardStore } from '@r1-compass-library/guards/save-guard.store';
import { injectParams } from 'ngxtension/inject-params';
import { ContentLibraryDefinitionForm } from '@r1-compass-library/models/content-library/content-library-definition-form.model';
import { ContentLibraryValidationService, ContentLibrarySectionFormMap } from '../content-library-validation.service';
import { ContentLibraryValidationStateService } from '../content-library-validation-state.service';
import {
    SyncValidationStateSection,
    ValidationService,
    ValidationStateService,
    SyncValidationStateService,
    ContentLibrarySectionName,
} from '@r1-compass-library/features/validation-sync';
import { AssetInvalidSaveWarningService } from '../../asset-invalid-save-warning.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'r1-compass-app-content-library-definition',
    imports: [
        InstructionsComponent,
        MatFormFieldModule,
        MatButtonModule,
        MatInputModule,
        MatOption,
        MatSelect,
        MatSlideToggle,
        ReactiveFormsModule,
    ],
    templateUrl: './content-library-definition.component.html',
    styleUrl: './content-library-definition.component.scss',
})
export class ContentLibraryDefinitionComponent
    implements
        OnInit,
        FormUnsavedChangesWarningEntity,
        SyncValidationStateSection<ContentLibrarySectionName, ContentAsset>
{
    readonly #contentAssetStore = inject(SelectedContentAssetStore);
    readonly #playService = inject(PlayService);
    readonly #route = inject(ActivatedRoute);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);
    readonly #assetStateMessageHandlingService = inject(AssetStateMessageHandlingService);
    readonly #formSaveWarningStore = inject(SaveGuardStore);
    readonly #validationService = inject(ContentLibraryValidationService);
    readonly #validationStateService = inject(ContentLibraryValidationStateService);
    readonly #syncValidationStateService = inject(SyncValidationStateService);
    readonly #invalidSaveWarningService = inject(AssetInvalidSaveWarningService);

    loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;
    defForm = new FormGroup<ContentLibraryDefinitionForm>(new ContentLibraryDefinitionForm());
    assetId: string | null = '';
    folderId: number | undefined;

    linkToPlayImporter = false;
    playImporterOptions: Pick<Play, 'id' | 'name'>[] | undefined = undefined;

    readonly canDeactivate$ = this.#formSaveWarningStore.deriveCanDeactivate$(this.defForm);
    readonly saveGuardHandlerFn = this.#formSaveWarningStore.formUnsavedChangesWarningEntityDefinition;

    readonly allowPartialSave = true;

    private readonly canDeactivate = toSignal(this.canDeactivate$, { requireSync: true });

    public disableSave = computed<boolean>(() =>
        this.#validationStateService.shouldDisableSave(
            this.#contentAssetStore.asset(),
            this.canDeactivate(),
            this.#validationStateService.invalidSections(),
            'definition'
        )
    );

    constructor() {
        this.saveGuardHandlerFn({
            form: this.defForm,
            canDeactivate$: this.canDeactivate$,
            params: injectParams('assetId'),
            entity$: this.#contentAssetStore.asset$,
            savedSoCanRoute$: this.#formSaveWarningStore.savedSoCanRoute$,
            route: this.#route,
            disableSave: this.disableSave,
        });
    }

    ngOnInit(): void {
        this.#contentAssetStore.asset$.pipe(filterNil(), take(1)).subscribe(asset => {
            this.defForm.patchValue({
                name: asset?.name,
                description: asset?.description,
                playImporterId: asset?.playImporterId,
            });
            this.onPlayImporterLink(!!asset.playImporterId);
        });

        this.subscribeToValidationState(
            this.defForm,
            'definition',
            this.#validationService,
            this.#validationStateService
        );
        this.defForm.markAllAsTouched(); // this makes the form indicate invalid fields on load

        this.#contentAssetStore.message$.pipe(filterNil()).subscribe(message => {
            this.#assetStateMessageHandlingService.handleMessage(message);
            this.#contentAssetStore.resetMessage();
        });
        this.#route.queryParams.subscribe(params => {
            this.folderId = Number(params['folderId'] as string);
        });
        this.assetId = this.#route.snapshot.paramMap.get('assetId');
        if (this.assetId && this.assetId !== 'new' && this.#contentAssetStore.asset()?.id !== +this.assetId) {
            this.#contentAssetStore.getAsset({ id: +this.assetId });
        }
    }

    onPlayImporterLink(linked: boolean) {
        this.linkToPlayImporter = linked;
        if (linked) {
            this.#playService
                .search({ type: 'Importer', deleted: 'False' })
                .pipe(
                    filterArray(play => Boolean(play.config?.playFileImport)),
                    take(1)
                )
                .subscribe(plays => {
                    this.playImporterOptions = plays.sort(function (a, b) {
                        return a.name!.localeCompare(b.name!);
                    });

                    // We only want to pick only valid plays
                    const playId = this.#contentAssetStore.asset()?.playImporterId;
                    if (playId !== undefined) {
                        if (!this.playImporterOptions.map(opt => opt.id).includes(playId)) {
                            this.defForm.controls.playImporterId.reset();
                        }
                    }
                });
            this.defForm.controls.playImporterId.setValidators([Validators.required]);
            this.defForm.controls.playImporterId.enable();
        } else {
            this.playImporterOptions = undefined;
            this.defForm.controls.playImporterId.reset();
            this.defForm.controls.playImporterId.disable();
        }
    }

    async onSave() {
        const iamDivisionId = this.#globalLoggedInUserStore.selectedIamDivision()?.id;
        if (iamDivisionId) {
            const formValue = this.defForm.getRawValue();
            const toSave: Partial<ContentAsset> = {
                ...this.#contentAssetStore.asset(),
                name: formValue.name,
                description: formValue.description,
                playImporterId: formValue.playImporterId,
                updatedBy: this.loggedInUserId(),
                valid: this.#validationStateService.invalidSections().length === 0,
            };
            if (this.assetId === 'new') {
                toSave.folderId = this.checkForRootFolder();
                toSave.iamDivisionId = this.#globalLoggedInUserStore.selectedIamDivisionId();
                toSave.createdBy = this.loggedInUserId();
            }
            if (!(await this.#invalidSaveWarningService.confirmSave('content asset', toSave))) {
                return noopFormSaveMutationResult();
            }
            return this.#contentAssetStore.saveAsset({
                asset: toSave,
                unlinkImporter: !!this.#contentAssetStore.asset()?.playImporterId && !toSave.playImporterId,
            });
        } else {
            return noopFormSaveMutationResult();
        }
    }

    subscribeToValidationState(
        form: FormGroup<ContentLibraryDefinitionForm>,
        sectionName: ContentLibrarySectionName,
        validationService: ValidationService<ContentLibrarySectionName, ContentAsset, ContentLibrarySectionFormMap>,
        validationStateService: ValidationStateService<ContentLibrarySectionName>
    ) {
        return this.#syncValidationStateService.subToValidationState(
            form,
            sectionName,
            validationService,
            validationStateService
        );
    }

    /**
     * Method for determining if asset is in Root folder or not.
     * Returns "undefined" if folderId is 0, as 0 is assumed to be Root.
     * Non-folder assets cannot have folderIds if they are created in Root, or else they will error due to database config.
     * @param id The current folderId
     * @private
     */
    private checkForRootFolder() {
        const id = Number(this.#route.snapshot.queryParams['folderId']);
        if (id === 0) {
            return undefined;
        } else {
            return id;
        }
    }
}
